import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  X, ChevronLeft, Calendar, Clock, MapPin, Users, Plus, FileText,
  CheckCircle, AlertTriangle, Target, BookOpen, Zap, Edit, Trash2,
  Sparkles, FileSpreadsheet, Lightbulb, Shield,
  Brain, Award, Search
} from 'lucide-react';
import { 
  toutesLesReunions, 
  genererResumeIA,
  type Reunion, 
  type OrdreDuJourItem,
  type Action
} from '../data/comiteTIReunionsData';

interface ComiteTIReunionsModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const ComiteTIReunionsModal: React.FC<ComiteTIReunionsModalProps> = ({ isOpen, onClose }) => {
  const [vueActive, setVueActive] = useState<'liste' | 'details'>('liste');
  const [reunionSelectionnee, setReunionSelectionnee] = useState<Reunion | null>(null);
  const [ongletActif, setOngletActif] = useState<'info' | 'agenda' | 'notes' | 'actions' | 'resume'>('info');
  const [showGuide, setShowGuide] = useState(false);
  const [showGuideIA, setShowGuideIA] = useState(false);
  const [isGeneratingIA, setIsGeneratingIA] = useState(false);
  const [verbatimTemp, setVerbatimTemp] = useState<string>('');

  if (!isOpen) return null;

  const handleOuvrirReunion = (reunion: Reunion) => {
    setReunionSelectionnee(reunion);
    setVueActive('details');
    setOngletActif('info');
    setVerbatimTemp(reunion.verbatim || '');
  };

  const handleRetourListe = () => {
    setVueActive('liste');
    setReunionSelectionnee(null);
  };

  const handleGenererAvecIA = async () => {
    if (!reunionSelectionnee) return;
    
    setIsGeneratingIA(true);
    
    // Simuler l'IA qui travaille (animation)
    await new Promise(resolve => setTimeout(resolve, 2000));
    
    // Générer le résumé
    const resumeGenere = genererResumeIA(reunionSelectionnee);
    
    // Mettre à jour la réunion
    reunionSelectionnee.resumeGenere = true;
    reunionSelectionnee.resumeContenu = resumeGenere;
    
    setIsGeneratingIA(false);
    
    // Rafraîchir l'affichage
    setReunionSelectionnee({ ...reunionSelectionnee });
  };

  const handleSauvegarderVerbatim = () => {
    if (!reunionSelectionnee) return;
    
    // Mettre à jour le verbatim
    reunionSelectionnee.verbatim = verbatimTemp;
    
    // Rafraîchir l'affichage
    setReunionSelectionnee({ ...reunionSelectionnee });
    
    // Feedback visuel (optionnel - vous pouvez ajouter un toast)
    alert('Verbatim sauvegardé avec succès!');
  };

  const getStatutBadge = (statut: Reunion['statut']) => {
    const config = {
      'Planifiée': { bg: 'bg-gray-100', text: 'text-gray-700', label: 'Planifiée' },
      'En cours': { bg: 'bg-blue-100', text: 'text-blue-700', label: 'En cours' },
      'Complétée': { bg: 'bg-green-100', text: 'text-green-700', label: 'Complétée' }
    };
    const { bg, text, label } = config[statut];
    return <span className={`${bg} ${text} px-3 py-1 rounded-full text-xs font-semibold`}>{label}</span>;
  };

  const getStatutODJBadge = (statut: OrdreDuJourItem['statut']) => {
    const config = {
      'Non traité': { bg: 'bg-gray-100', text: 'text-gray-700' },
      'En discussion': { bg: 'bg-blue-500', text: 'text-white' },
      'Complété': { bg: 'bg-green-500', text: 'text-white' },
      'Reporté': { bg: 'bg-orange-500', text: 'text-white' }
    };
    const { bg, text } = config[statut];
    return <span className={`${bg} ${text} px-3 py-1 rounded-full text-xs font-semibold`}>{statut}</span>;
  };

  const getStatutActionBadge = (statut: Action['statut']) => {
    const config = {
      'À faire': { bg: 'bg-gray-100', text: 'text-gray-700' },
      'En cours': { bg: 'bg-blue-500', text: 'text-white' },
      'Complété': { bg: 'bg-green-500', text: 'text-white' },
      'Bloqué': { bg: 'bg-red-500', text: 'text-white' }
    };
    const { bg, text } = config[statut];
    return <span className={`${bg} ${text} px-2 py-1 rounded text-xs font-medium`}>{statut}</span>;
  };

  const getPrioriteBadge = (priorite: Action['priorite']) => {
    const config = {
      'Basse': { bg: 'bg-gray-400', text: 'text-white' },
      'Moyenne': { bg: 'bg-blue-500', text: 'text-white' },
      'Haute': { bg: 'bg-orange-500', text: 'text-white' },
      'Critique': { bg: 'bg-red-600', text: 'text-white' }
    };
    const { bg, text } = config[priorite];
    return <span className={`${bg} ${text} px-2 py-1 rounded text-xs font-bold`}>{priorite}</span>;
  };

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        {/* Overlay */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="absolute inset-0 bg-black/70 backdrop-blur-sm"
          onClick={onClose}
        />

        {/* Modal */}
        <motion.div
          initial={{ scale: 0.9, opacity: 0, y: 20 }}
          animate={{ scale: 1, opacity: 1, y: 0 }}
          exit={{ scale: 0.9, opacity: 0, y: 20 }}
          transition={{ duration: 0.3 }}
          className="relative w-[95vw] h-[90vh] bg-white rounded-2xl shadow-2xl overflow-hidden flex flex-col"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="relative bg-gradient-to-r from-green-600 via-teal-600 to-blue-600 p-6">
            <div className="absolute inset-0 bg-grid-white/10 pointer-events-none" />
            <button
              onClick={onClose}
              className="absolute top-4 right-4 z-10 p-2 bg-white/20 hover:bg-white/30 rounded-xl transition-colors backdrop-blur-sm cursor-pointer"
            >
              <X className="w-6 h-6 text-white" />
            </button>

            <div className="relative flex items-center gap-4">
              <div className="bg-white/20 backdrop-blur-xl p-4 rounded-2xl">
                <Calendar className="w-10 h-10 text-white" />
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-1">
                  <h1 className="text-3xl font-black text-white">
                    {vueActive === 'liste' ? 'Comité TI - Pilotage Stratégique' : reunionSelectionnee?.titre}
                  </h1>
                  {/* Badge AI - Disponible dans AI Assistant */}
                  <motion.div
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ delay: 0.3, type: "spring", stiffness: 200 }}
                    className="flex items-center gap-1.5 px-3 py-1.5 backdrop-blur-sm bg-purple-500/40 border border-purple-400/60 rounded-full shadow-lg shadow-purple-500/20"
                    title="Disponible dans AI Assistant"
                  >
                    <Brain className="w-4 h-4 text-purple-200" />
                    <span className="text-xs text-purple-100 font-semibold">AI</span>
                  </motion.div>
                </div>
                <p className="text-white/90 text-lg">
                  {vueActive === 'liste' 
                    ? `${toutesLesReunions.length} réunion(s) enregistrée(s)` 
                    : `Réunion du ${reunionSelectionnee ? new Date(reunionSelectionnee.date).toLocaleDateString('fr-FR') : ''}`
                  }
                </p>
              </div>
            </div>
          </div>

          {/* VUE LISTE */}
          {vueActive === 'liste' && (
            <div className="flex-1 overflow-y-auto p-8">
              <div className="flex justify-end mb-6">
                <button className="flex items-center gap-2 px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-xl transition-all shadow-lg">
                  <Plus className="w-5 h-5" />
                  Nouvelle Réunion
                </button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {toutesLesReunions.map((reunion) => (
                  <motion.div
                    key={reunion.id}
                    whileHover={{ scale: 1.02 }}
                    className="bg-white border-2 border-gray-200 rounded-2xl p-6 cursor-pointer hover:shadow-xl transition-all"
                    onClick={() => handleOuvrirReunion(reunion)}
                  >
                    <div className="flex justify-between items-start mb-4">
                      <div className="text-sm text-gray-500">
                        {new Date(reunion.date).toLocaleDateString('fr-FR', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
                      </div>
                      {getStatutBadge(reunion.statut)}
                    </div>

                    <h3 className="text-xl font-bold text-gray-900 mb-4 break-words leading-snug">{reunion.titre}</h3>

                    <div className="space-y-2 text-sm text-gray-600 mb-4">
                      <div className="flex min-w-0 items-center gap-2">
                        <Clock className="w-4 h-4 shrink-0" />
                        <span className="min-w-0 break-words">
                          {reunion.heure} ({reunion.dureeMinutes} min)
                        </span>
                      </div>
                      <div className="flex min-w-0 items-start gap-2">
                        <MapPin className="w-4 h-4 shrink-0 mt-0.5" />
                        <span className="min-w-0 break-words">{reunion.lieu}</span>
                      </div>
                      <div className="flex min-w-0 items-start gap-2">
                        <FileText className="w-4 h-4 shrink-0 mt-0.5" />
                        <span className="min-w-0 break-words">{reunion.objectifPrincipal}</span>
                      </div>
                    </div>

                    <div className="flex flex-wrap justify-between gap-x-2 gap-y-2 text-xs text-gray-500 pt-4 border-t">
                      <div className="flex items-center gap-1">
                        <Users className="w-4 h-4" />
                        <span className={reunion.participants.filter(p => p.present).length > 0 ? 'text-blue-600 font-semibold' : ''}>
                          {reunion.participants.filter(p => p.present).length} participant{reunion.participants.length > 1 ? 's' : ''}
                        </span>
                      </div>
                      <div className="flex items-center gap-1">
                        <BookOpen className="w-4 h-4" />
                        <span className={reunion.notes.length > 0 ? 'text-orange-600 font-semibold' : ''}>
                          {reunion.notes.length} note{reunion.notes.length > 1 ? 's' : ''}
                        </span>
                      </div>
                      <div className="flex items-center gap-1">
                        <FileText className="w-4 h-4" />
                        <span className={reunion.ordreDuJour.length > 0 ? 'text-purple-600 font-semibold' : ''}>
                          {reunion.ordreDuJour.length} point{reunion.ordreDuJour.length > 1 ? 's' : ''}
                        </span>
                      </div>
                      <div className="flex items-center gap-1">
                        <CheckCircle className="w-4 h-4" />
                        <span className={reunion.actions.length > 0 ? 'text-red-600 font-semibold' : ''}>
                          {reunion.actions.length} action{reunion.actions.length > 1 ? 's' : ''}
                        </span>
                      </div>
                    </div>

                    {reunion.resumeGenere && (
                      <div className="mt-4 flex items-center gap-2 text-green-600 text-sm font-semibold">
                        <Sparkles className="w-4 h-4" />
                        Résumé complété
                      </div>
                    )}
                    {!reunion.resumeGenere && reunion.statut === 'Complétée' && (
                      <div className="mt-4 text-gray-400 text-sm">
                        Résumé non généré
                      </div>
                    )}
                  </motion.div>
                ))}
              </div>
            </div>
          )}

          {/* VUE DÉTAILS */}
          {vueActive === 'details' && reunionSelectionnee && (
            <div className="flex-1 flex flex-col overflow-hidden">
              {/* Navigation retour + onglets */}
              <div className="border-b border-gray-200 bg-white px-6 py-3">
                <button
                  onClick={handleRetourListe}
                  className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-3 transition-colors"
                >
                  <ChevronLeft className="w-4 h-4" />
                  Retour à la liste
                </button>

                <div className="flex gap-2">
                  {[
                    { id: 'info', label: 'Info & Participants', icon: Users },
                    { id: 'agenda', label: 'Ordre du Jour', icon: FileText },
                    { id: 'notes', label: 'Notes/Décisions/Enjeux', icon: BookOpen },
                    { id: 'actions', label: 'Actions/TODOS', icon: CheckCircle },
                    { id: 'resume', label: 'Résumé', icon: Zap }
                  ].map((onglet) => {
                    const Icon = onglet.icon;
                    return (
                      <button
                        key={onglet.id}
                        onClick={() => setOngletActif(onglet.id as any)}
                        className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-all font-medium ${
                          ongletActif === onglet.id
                            ? 'bg-green-600 text-white shadow-lg'
                            : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                        }`}
                      >
                        <Icon className="w-4 h-4" />
                        {onglet.label}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Contenu des onglets */}
              <div className="flex-1 overflow-y-auto p-8">
                {/* ONGLET INFO & PARTICIPANTS */}
                {ongletActif === 'info' && (
                  <div className="space-y-6">
                    {/* Informations réunion */}
                    <div className="bg-white border-2 border-gray-200 rounded-2xl p-6">
                      <div className="flex justify-between items-center mb-6">
                        <h3 className="text-xl font-bold flex items-center gap-2">
                          <Calendar className="w-6 h-6 text-blue-600" />
                          Informations de la Réunion
                        </h3>
                        <div className="flex gap-2">
                          <button className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors">
                            <Edit className="w-4 h-4" />
                            Modifier
                          </button>
                          <button className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors">
                            <Trash2 className="w-4 h-4" />
                            Supprimer
                          </button>
                        </div>
                      </div>

                      <div className="grid grid-cols-2 gap-6">
                        <div>
                          <div className="text-sm text-gray-500 mb-1">Nom de la réunion</div>
                          <div className="font-semibold text-gray-900">{reunionSelectionnee.titre}</div>
                        </div>
                        <div>
                          <div className="text-sm text-gray-500 mb-1">Date</div>
                          <div className="font-semibold text-gray-900">
                            {new Date(reunionSelectionnee.date).toLocaleDateString('fr-FR', { 
                              weekday: 'long', 
                              year: 'numeric', 
                              month: 'long', 
                              day: 'numeric' 
                            })}
                          </div>
                        </div>
                        <div>
                          <div className="text-sm text-gray-500 mb-1">Heure</div>
                          <div className="font-semibold text-gray-900">{reunionSelectionnee.heure}</div>
                        </div>
                        <div>
                          <div className="text-sm text-gray-500 mb-1">Durée</div>
                          <div className="font-semibold text-gray-900">{reunionSelectionnee.dureeMinutes} minutes</div>
                        </div>
                        <div>
                          <div className="text-sm text-gray-500 mb-1">Lieu</div>
                          <div className="font-semibold text-gray-900">{reunionSelectionnee.lieu}</div>
                        </div>
                        <div>
                          <div className="text-sm text-gray-500 mb-1">Statut</div>
                          <div>{getStatutBadge(reunionSelectionnee.statut)}</div>
                        </div>
                        <div className="col-span-2">
                          <div className="text-sm text-gray-500 mb-1">Objectif principal</div>
                          <div className="font-semibold text-gray-900">{reunionSelectionnee.objectifPrincipal}</div>
                        </div>
                      </div>
                    </div>

                    {/* Participants */}
                    <div className="bg-white border-2 border-gray-200 rounded-2xl p-6">
                      <div className="flex justify-between items-center mb-6">
                        <h3 className="text-xl font-bold flex items-center gap-2">
                          <Users className="w-6 h-6 text-blue-600" />
                          Participants ({reunionSelectionnee.participants.filter(p => p.present).length})
                        </h3>
                        <button className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors">
                          <Plus className="w-4 h-4" />
                          Ajouter
                        </button>
                      </div>

                      <div className="grid grid-cols-2 gap-4">
                        {reunionSelectionnee.participants.map((participant) => (
                          <div key={participant.id} className="border border-gray-200 rounded-lg p-4">
                            <div className="font-semibold text-gray-900">{participant.prenom} {participant.nom}</div>
                            <div className="text-sm text-gray-500 mb-2">{participant.role}</div>
                            <div className="text-sm text-blue-600 mb-3">{participant.email}</div>
                            <div className="flex items-center gap-3 text-sm">
                              <span className="text-gray-600">Statut de présence:</span>
                              <label className="relative inline-flex items-center cursor-pointer">
                                <input
                                  type="checkbox"
                                  checked={participant.present}
                                  className="sr-only peer"
                                  readOnly
                                />
                                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-green-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-green-600"></div>
                              </label>
                              <span className={participant.present ? 'text-green-600 font-semibold' : 'text-gray-500'}>
                                {participant.present ? '✓ Présent' : 'Absent'}
                              </span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                )}

                {/* ONGLET ORDRE DU JOUR */}
                {ongletActif === 'agenda' && (
                  <div className="space-y-6">
                    <div className="bg-white border-2 border-gray-200 rounded-2xl p-6">
                      <div className="flex justify-between items-center mb-6">
                        <div>
                          <h3 className="text-xl font-bold flex items-center gap-2">
                            <FileText className="w-6 h-6 text-green-600" />
                            Ordre du Jour
                          </h3>
                          <p className="text-sm text-gray-500 mt-1">
                            {reunionSelectionnee.ordreDuJour.length} item(s) • Durée totale: {reunionSelectionnee.ordreDuJour.reduce((sum, item) => sum + item.dureeMinutes, 0)} minutes
                          </p>
                        </div>
                        <button className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors">
                          <Plus className="w-4 h-4" />
                          Ajouter un item
                        </button>
                      </div>

                      <div className="space-y-4">
                        {reunionSelectionnee.ordreDuJour.map((item) => (
                          <div key={item.id} className="border-l-4 border-green-500 bg-gray-50 rounded-lg p-4">
                            <div className="flex justify-between items-start">
                              <div className="flex gap-3 flex-1">
                                <div className="bg-green-600 text-white rounded-full w-8 h-8 flex items-center justify-center font-bold flex-shrink-0">
                                  {item.numero}
                                </div>
                                <div className="flex-1">
                                  <h4 className="font-bold text-gray-900 mb-2">{item.titre}</h4>
                                  <p className="text-sm text-gray-600 mb-3">{item.description}</p>
                                  <div className="flex items-center gap-4 text-sm">
                                    <div className="flex items-center gap-1 text-gray-600">
                                      <Clock className="w-4 h-4" />
                                      {item.dureeMinutes} min
                                    </div>
                                    <div className="flex items-center gap-1 text-gray-600">
                                      <Users className="w-4 h-4" />
                                      {item.responsable}
                                    </div>
                                  </div>
                                </div>
                              </div>
                              {getStatutODJBadge(item.statut)}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                )}

                {/* ONGLET NOTES/DÉCISIONS/ENJEUX */}
                {ongletActif === 'notes' && (
                  <div className="space-y-6">
                    <div className="bg-white border-2 border-gray-200 rounded-2xl p-6">
                      <div className="flex justify-between items-center mb-6">
                        <h3 className="text-xl font-bold flex items-center gap-2">
                          <BookOpen className="w-6 h-6 text-blue-600" />
                          Notes de Réunion
                        </h3>
                        <div className="flex gap-2">
                          <button
                            onClick={() => setShowGuide(true)}
                            className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
                          >
                            <Lightbulb className="w-4 h-4" />
                            Guide
                          </button>
                          <button className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors">
                            <Plus className="w-4 h-4" />
                            Ajouter
                          </button>
                        </div>
                      </div>

                      {/* Notes Générales */}
                      {reunionSelectionnee.notes.filter(n => n.type === 'generale').length > 0 && (
                        <div className="mb-6">
                          <h4 className="font-bold text-gray-900 mb-3 flex items-center gap-2">
                            <BookOpen className="w-5 h-5 text-blue-600" />
                            Notes Générales ({reunionSelectionnee.notes.filter(n => n.type === 'generale').length})
                          </h4>
                          <div className="space-y-3">
                            {reunionSelectionnee.notes.filter(n => n.type === 'generale').map((note) => (
                              <div key={note.id} className="border-l-4 border-blue-500 bg-blue-50 rounded-lg p-4">
                                <p className="text-gray-700 mb-2">{note.contenu}</p>
                                <div className="text-xs text-gray-500">
                                  — {note.auteur} • {new Date(note.timestamp).toLocaleString('fr-FR')}
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Décisions */}
                      {reunionSelectionnee.notes.filter(n => n.type === 'decision').length > 0 && (
                        <div className="mb-6">
                          <h4 className="font-bold text-gray-900 mb-3 flex items-center gap-2">
                            <CheckCircle className="w-5 h-5 text-green-600" />
                            Décisions ({reunionSelectionnee.notes.filter(n => n.type === 'decision').length})
                          </h4>
                          <div className="space-y-3">
                            {reunionSelectionnee.notes.filter(n => n.type === 'decision').map((note) => (
                              <div key={note.id} className="border-l-4 border-green-500 bg-green-50 rounded-lg p-4">
                                <p className="text-gray-700 mb-2 font-medium">{note.contenu}</p>
                                <div className="text-xs text-gray-500">
                                  — {note.auteur} • {new Date(note.timestamp).toLocaleString('fr-FR')}
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Enjeux */}
                      {reunionSelectionnee.notes.filter(n => n.type === 'enjeu').length > 0 && (
                        <div className="mb-6">
                          <h4 className="font-bold text-gray-900 mb-3 flex items-center gap-2">
                            <AlertTriangle className="w-5 h-5 text-orange-600" />
                            Enjeux ({reunionSelectionnee.notes.filter(n => n.type === 'enjeu').length})
                          </h4>
                          <div className="space-y-3">
                            {reunionSelectionnee.notes.filter(n => n.type === 'enjeu').map((note) => (
                              <div key={note.id} className="border-l-4 border-orange-500 bg-orange-50 rounded-lg p-4">
                                <p className="text-gray-700 mb-2 font-medium">{note.contenu}</p>
                                <div className="text-xs text-gray-500">
                                  — {note.auteur} • {new Date(note.timestamp).toLocaleString('fr-FR')}
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Risques */}
                      {reunionSelectionnee.notes.filter(n => n.type === 'risque').length > 0 && (
                        <div className="mb-6">
                          <h4 className="font-bold text-gray-900 mb-3 flex items-center gap-2">
                            <Target className="w-5 h-5 text-red-600" />
                            Risques ({reunionSelectionnee.notes.filter(n => n.type === 'risque').length})
                          </h4>
                          <div className="space-y-3">
                            {reunionSelectionnee.notes.filter(n => n.type === 'risque').map((note) => (
                              <div key={note.id} className="border-l-4 border-red-500 bg-red-50 rounded-lg p-4">
                                <p className="text-gray-700 mb-2 font-medium">{note.contenu}</p>
                                <div className="text-xs text-gray-500">
                                  — {note.auteur} • {new Date(note.timestamp).toLocaleString('fr-FR')}
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {reunionSelectionnee.notes.length === 0 && (
                        <div className="text-center py-8 text-gray-500">
                          Aucune note enregistrée pour cette réunion
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* ONGLET ACTIONS/TODOS */}
                {ongletActif === 'actions' && (
                  <div className="space-y-6">
                    <div className="bg-white border-2 border-gray-200 rounded-2xl p-6">
                      <div className="flex justify-between items-center mb-6">
                        <div>
                          <h3 className="text-xl font-bold flex items-center gap-2">
                            <CheckCircle className="w-6 h-6 text-purple-600" />
                            Actions & TODOS
                          </h3>
                          <p className="text-sm text-gray-500 mt-1">
                            {reunionSelectionnee.actions.filter(a => a.statut === 'Complété').length}/{reunionSelectionnee.actions.length} complétées • {Math.round((reunionSelectionnee.actions.filter(a => a.statut === 'Complété').length / (reunionSelectionnee.actions.length || 1)) * 100)}% de progression
                          </p>
                        </div>
                        <button className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors">
                          <Plus className="w-4 h-4" />
                          Ajouter une action
                        </button>
                      </div>

                      {/* Barre de progression */}
                      {reunionSelectionnee.actions.length > 0 && (
                        <div className="mb-6">
                          <div className="w-full bg-gray-200 rounded-full h-3">
                            <div 
                              className="bg-gradient-to-r from-purple-600 to-pink-600 h-3 rounded-full transition-all"
                              style={{ 
                                width: `${(reunionSelectionnee.actions.filter(a => a.statut === 'Complété').length / reunionSelectionnee.actions.length) * 100}%` 
                              }}
                            />
                          </div>
                        </div>
                      )}

                      {/* Grouper par statut */}
                      {['En cours', 'À faire', 'Bloqué', 'Complété'].map((statut) => {
                        const actions = reunionSelectionnee.actions.filter(a => a.statut === statut);
                        if (actions.length === 0) return null;

                        const statutConfig = {
                          'En cours': { bg: 'bg-blue-50', border: 'border-blue-500', title: 'text-blue-700' },
                          'À faire': { bg: 'bg-gray-50', border: 'border-gray-400', title: 'text-gray-700' },
                          'Bloqué': { bg: 'bg-red-50', border: 'border-red-500', title: 'text-red-700' },
                          'Complété': { bg: 'bg-green-50', border: 'border-green-500', title: 'text-green-700' }
                        };

                        const config = statutConfig[statut as keyof typeof statutConfig];

                        return (
                          <div key={statut} className="mb-6">
                            <h4 className={`font-bold mb-3 flex items-center gap-2 ${config.title}`}>
                              {statut}
                              <span className="bg-gray-200 text-gray-700 text-xs px-2 py-1 rounded-full">{actions.length}</span>
                            </h4>
                            <div className="space-y-3">
                              {actions.map((action) => (
                                <div key={action.id} className={`border-l-4 ${config.border} ${config.bg} rounded-lg p-4`}>
                                  <div className="flex justify-between items-start mb-2">
                                    <h5 className="font-bold text-gray-900">{action.titre}</h5>
                                    <div className="flex gap-2">
                                      {getPrioriteBadge(action.priorite)}
                                      {getStatutActionBadge(action.statut)}
                                    </div>
                                  </div>
                                  <p className="text-sm text-gray-600 mb-3">{action.description}</p>
                                  <div className="flex items-center gap-4 text-sm text-gray-600">
                                    <div className="flex items-center gap-1">
                                      <Users className="w-4 h-4" />
                                      {action.responsable}
                                    </div>
                                    <div className="flex items-center gap-1">
                                      <Calendar className="w-4 h-4" />
                                      Échéance: {new Date(action.echeance).toLocaleDateString('fr-FR')}
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        );
                      })}

                      {reunionSelectionnee.actions.length === 0 && (
                        <div className="text-center py-8 text-gray-500">
                          Aucune action définie pour cette réunion
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* ONGLET RÉSUMÉ */}
                {ongletActif === 'resume' && (
                  <div className="space-y-6">
                    <div className="bg-white border-2 border-gray-200 rounded-2xl p-6">
                      <div className="flex justify-between items-center mb-6">
                        <h3 className="text-xl font-bold flex items-center gap-2">
                          <Sparkles className="w-6 h-6 text-purple-600" />
                          Résumé de la Réunion
                        </h3>
                        <div className="flex gap-2">
                          <button
                            onClick={() => setShowGuideIA(true)}
                            className="flex items-center gap-2 px-4 py-2 bg-white border-2 border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                          >
                            <FileSpreadsheet className="w-4 h-4" />
                            Guide IA
                          </button>
                          {!reunionSelectionnee.resumeGenere && (
                            <button
                              onClick={handleGenererAvecIA}
                              disabled={isGeneratingIA || reunionSelectionnee.notes.length === 0}
                              className={`flex items-center gap-2 px-6 py-2 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-lg transition-all shadow-lg ${
                                isGeneratingIA || reunionSelectionnee.notes.length === 0
                                  ? 'opacity-50 cursor-not-allowed'
                                  : 'hover:shadow-xl hover:scale-105'
                              }`}
                            >
                              {isGeneratingIA ? (
                                <>
                                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
                                  Génération en cours...
                                </>
                              ) : (
                                <>
                                  <Sparkles className="w-4 h-4" />
                                  Générer avec IA
                                </>
                              )}
                            </button>
                          )}
                          {reunionSelectionnee.resumeGenere && (
                            <button className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors">
                              <Edit className="w-4 h-4" />
                              Modifier
                            </button>
                          )}
                        </div>
                      </div>

                      {reunionSelectionnee.resumeGenere && reunionSelectionnee.resumeContenu ? (
                        <div className="border-2 border-purple-200 rounded-xl p-6 bg-purple-50/30">
                          <div className="flex items-center justify-between mb-4">
                            <h4 className="font-bold text-lg flex items-center gap-2">
                              <Sparkles className="w-5 h-5 text-purple-600" />
                              Résumé Professionnel
                            </h4>
                            <span className="text-xs bg-purple-100 text-purple-700 px-3 py-1 rounded-full flex items-center gap-1">
                              <Sparkles className="w-3 h-3" />
                              Généré par IA, approuvé par humain
                            </span>
                          </div>
                          <div className="prose max-w-none">
                            <pre className="whitespace-pre-wrap font-sans text-sm text-gray-700 leading-relaxed">
                              {reunionSelectionnee.resumeContenu}
                            </pre>
                          </div>
                        </div>
                      ) : (
                        <div className="text-center py-12">
                          {reunionSelectionnee.notes.length === 0 ? (
                            <div className="text-gray-500">
                              <Brain className="w-16 h-16 mx-auto mb-4 text-gray-300" />
                              <p className="text-lg font-medium mb-2">Aucune note disponible</p>
                              <p className="text-sm">Ajoutez des notes pour pouvoir générer un résumé avec l'IA</p>
                            </div>
                          ) : (
                            <div className="text-gray-500">
                              <Sparkles className="w-16 h-16 mx-auto mb-4 text-purple-300" />
                              <p className="text-lg font-medium mb-2">Résumé non généré</p>
                              <p className="text-sm">Cliquez sur "Générer avec IA" pour créer un résumé professionnel</p>
                            </div>
                          )}
                        </div>
                      )}
                    </div>

                    {/* GUIDE: UN BON RÉSUMÉ CONTIENT */}
                    <div className="bg-gradient-to-br from-yellow-50 to-orange-50 border-2 border-yellow-300 rounded-2xl p-6">
                      <div className="flex items-start gap-3 mb-4">
                        <div className="bg-yellow-400 rounded-full p-2">
                          <Lightbulb className="w-6 h-6 text-white" />
                        </div>
                        <div>
                          <h3 className="text-xl font-bold text-gray-900 mb-1">Un bon résumé contient:</h3>
                          <p className="text-sm text-gray-600">Guide de référence pour les réunions Direction TI - Bonnes pratiques ITIL & Standards de gouvernance</p>
                        </div>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {/* Contexte et Objectifs */}
                        <div className="bg-white border border-blue-200 rounded-xl p-4">
                          <div className="flex items-center gap-2 mb-3">
                            <Target className="w-5 h-5 text-blue-600" />
                            <h4 className="font-bold text-gray-900">Contexte et objectifs de la réunion</h4>
                          </div>
                          <ul className="text-sm text-gray-700 space-y-1">
                            <li>• Pourquoi cette réunion? Quel était le besoin?</li>
                            <li>• Quels étaient les objectifs stratégiques?</li>
                            <li className="text-red-600 font-semibold">⚠️ Piège: Ne pas contextualiser = perte de valeur historique</li>
                          </ul>
                        </div>

                        {/* Décisions prises */}
                        <div className="bg-white border border-green-200 rounded-xl p-4">
                          <div className="flex items-center gap-2 mb-3">
                            <CheckCircle className="w-5 h-5 text-green-600" />
                            <h4 className="font-bold text-gray-900">Décisions prises et par qui</h4>
                          </div>
                          <ul className="text-sm text-gray-700 space-y-1">
                            <li>• QUI a décidé (nom, rôle)</li>
                            <li>• QUOI a été décidé (décision précise)</li>
                            <li>• QUAND (date/heure)</li>
                            <li className="text-red-600 font-semibold">⚠️ Piège: Décision floue sans responsable = non-décision</li>
                          </ul>
                        </div>

                        {/* Enjeux et risques */}
                        <div className="bg-white border border-orange-200 rounded-xl p-4">
                          <div className="flex items-center gap-2 mb-3">
                            <AlertTriangle className="w-5 h-5 text-orange-600" />
                            <h4 className="font-bold text-gray-900">Enjeux et risques identifiés</h4>
                          </div>
                          <ul className="text-sm text-gray-700 space-y-1">
                            <li>• Contraintes (budget, ressources, temps)</li>
                            <li>• Dépendances critiques</li>
                            <li>• Risques TI (sécurité, performance, conformité)</li>
                            <li className="text-red-600 font-semibold">⚠️ Piège: Cacher les risques = explosion en prod</li>
                          </ul>
                        </div>

                        {/* Actions avec responsables */}
                        <div className="bg-white border border-purple-200 rounded-xl p-4">
                          <div className="flex items-center gap-2 mb-3">
                            <Users className="w-5 h-5 text-purple-600" />
                            <h4 className="font-bold text-gray-900">Actions décidées avec responsables et échéances</h4>
                          </div>
                          <ul className="text-sm text-gray-700 space-y-1">
                            <li>• Action précise (verbe d'action)</li>
                            <li>• Responsable unique (pas "l'équipe")</li>
                            <li>• Date d'échéance réaliste</li>
                            <li className="text-red-600 font-semibold">⚠️ Piège: Responsabilité diluée = rien n'avance</li>
                          </ul>
                        </div>

                        {/* Prochaines étapes */}
                        <div className="bg-white border border-indigo-200 rounded-xl p-4">
                          <div className="flex items-center gap-2 mb-3">
                            <Calendar className="w-5 h-5 text-indigo-600" />
                            <h4 className="font-bold text-gray-900">Prochaines étapes</h4>
                          </div>
                          <ul className="text-sm text-gray-700 space-y-1">
                            <li>• Date de la prochaine réunion</li>
                            <li>• Sujets à préparer/apporter</li>
                            <li>• Communication aux parties prenantes</li>
                            <li className="text-red-600 font-semibold">⚠️ Piège: Pas de suivi = perte de momentum</li>
                          </ul>
                        </div>

                        {/* KPIs et métriques */}
                        <div className="bg-white border border-pink-200 rounded-xl p-4">
                          <div className="flex items-center gap-2 mb-3">
                            <FileSpreadsheet className="w-5 h-5 text-pink-600" />
                            <h4 className="font-bold text-gray-900">KPIs et métriques (si applicable)</h4>
                          </div>
                          <ul className="text-sm text-gray-700 space-y-1">
                            <li>• Résultats chiffrés discutés</li>
                            <li>• Écarts vs objectifs</li>
                            <li>• Tendances et analyse</li>
                            <li className="text-green-600 font-semibold">✅ Best practice ITIL: Mesurer pour améliorer</li>
                          </ul>
                        </div>
                      </div>

                      {/* Standards et conseils */}
                      <div className="mt-6 bg-gradient-to-r from-blue-100 to-purple-100 border border-blue-300 rounded-xl p-4">
                        <h4 className="font-bold text-gray-900 mb-3 flex items-center gap-2">
                          <Award className="w-5 h-5 text-blue-600" />
                          Standards & Conseils Professionnels (ITIL, PMI, COBIT)
                        </h4>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                          <div>
                            <span className="font-semibold text-blue-700">📘 ITIL 4:</span>
                            <ul className="text-gray-700 ml-4">
                              <li>• Focus sur la valeur client</li>
                              <li>• Documenter les décisions de changement</li>
                              <li>• Traçabilité des incidents/problèmes</li>
                            </ul>
                          </div>
                          <div>
                            <span className="font-semibold text-purple-700">🎯 PMI (Gestion de projet):</span>
                            <ul className="text-gray-700 ml-4">
                              <li>• Identifier risques et mitigation</li>
                              <li>• Actions SMART (Spécifiques, Mesurables, etc.)</li>
                              <li>• Registre des décisions</li>
                            </ul>
                          </div>
                          <div>
                            <span className="font-semibold text-green-700">🔐 COBIT:</span>
                            <ul className="text-gray-700 ml-4">
                              <li>• Gouvernance TI alignée business</li>
                              <li>• Traçabilité pour audits</li>
                              <li>• Conformité réglementaire</li>
                            </ul>
                          </div>
                          <div>
                            <span className="font-semibold text-orange-700">⚡ Conseils BZ:</span>
                            <ul className="text-gray-700 ml-4">
                              <li>• Résumé dans les 48h suivant la réunion</li>
                              <li>• Max 2 pages (lisible en 3-5 min)</li>
                              <li>• Validé par le président de comité</li>
                            </ul>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* VERBATIM (TRANSCRIPTION TEAMS) */}
                    <div className="bg-white border-2 border-gray-300 rounded-2xl p-6">
                      <div className="flex items-start gap-3 mb-4">
                        <div className="bg-gray-700 rounded-full p-2">
                          <FileText className="w-6 h-6 text-white" />
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <h3 className="text-xl font-bold text-gray-900">Verbatim (Transcription Teams)</h3>
                            <span className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded">?</span>
                          </div>
                          <p className="text-sm text-gray-600">
                            Collez ici la transcription complète de votre réunion Teams pour assurer une transparence totale. 
                            Cette archive permet de vérifier le résumé IA/humain si nécessaire.
                          </p>
                        </div>
                      </div>

                      <textarea
                        value={verbatimTemp}
                        onChange={(e) => setVerbatimTemp(e.target.value)}
                        placeholder="Collez ici la transcription complète de votre réunion Teams...

Exemple:
[09:00] Sophie Bolduc : Bonjour à tous, bienvenue à notre comité TI...
[09:05] Eric Hains : Merci Sophie, voici les points principaux...
..."
                        className="w-full h-64 p-4 border-2 border-gray-300 rounded-xl text-sm font-mono resize-y focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      />

                      <div className="flex justify-between items-center mt-4">
                        <div className="flex items-start gap-2 text-xs text-gray-500">
                          <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                          <div>
                            <p className="font-semibold">Archive de référence: Le verbatim est conservé pour consultation.</p>
                            <p>L'IA génère le résumé à partir de vos <strong>notes structurées</strong>, pas du verbatim brut.</p>
                          </div>
                        </div>
                        <button
                          onClick={handleSauvegarderVerbatim}
                          className="flex items-center gap-2 px-6 py-2 bg-gray-700 hover:bg-gray-800 text-white font-semibold rounded-lg transition-colors shadow-lg"
                        >
                          <FileText className="w-4 h-4" />
                          Sauvegarder
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* GUIDE BONNES PRATIQUES MODAL */}
          {showGuide && (
            <div className="absolute inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={() => setShowGuide(false)}>
              <motion.div
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                className="bg-white rounded-2xl p-8 max-w-4xl w-full max-h-[80vh] overflow-y-auto"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="flex justify-between items-center mb-6">
                  <h3 className="text-2xl font-bold flex items-center gap-2">
                    <Lightbulb className="w-7 h-7 text-purple-600" />
                    Guide des bonnes pratiques
                  </h3>
                  <button onClick={() => setShowGuide(false)} className="p-2 hover:bg-gray-100 rounded-lg">
                    <X className="w-5 h-5" />
                  </button>
                </div>
                
                <p className="text-gray-600 mb-6">Comment documenter efficacement vos réunions</p>

                <div className="space-y-6">
                  <div className="bg-blue-50 border-2 border-blue-200 rounded-xl p-6">
                    <h4 className="font-bold text-lg mb-3 flex items-center gap-2">
                      <BookOpen className="w-5 h-5 text-blue-600" />
                      Notes Générales
                    </h4>
                    <p className="text-sm text-gray-700 mb-2">Ce qui se dit pendant la réunion:</p>
                    <ul className="space-y-2 text-sm text-gray-700">
                      <li>• Discussions importantes et contexte</li>
                      <li>• Points de vue exprimés par les participants</li>
                      <li>• Informations partagées et explications</li>
                    </ul>
                  </div>

                  <div className="bg-green-50 border-2 border-green-200 rounded-xl p-6">
                    <h4 className="font-bold text-lg mb-3 flex items-center gap-2">
                      <CheckCircle className="w-5 h-5 text-green-600" />
                      Décisions
                    </h4>
                    <p className="text-sm text-gray-700 mb-2">Ce qui est approuvé ou choisi:</p>
                    <ul className="space-y-2 text-sm text-gray-700">
                      <li className="flex items-start gap-2">
                        <CheckCircle className="w-4 h-4 text-green-600 mt-0.5" />
                        Ex: "Nous approuvons le budget de 50K$ pour le projet X"
                      </li>
                      <li className="flex items-start gap-2">
                        <CheckCircle className="w-4 h-4 text-green-600 mt-0.5" />
                        Ex: "On retient le fournisseur ABC pour la migration cloud"
                      </li>
                      <li className="font-semibold text-green-700">Important: Toujours noter QUI décide et QUAND</li>
                    </ul>
                  </div>

                  <div className="bg-orange-50 border-2 border-orange-200 rounded-xl p-6">
                    <h4 className="font-bold text-lg mb-3 flex items-center gap-2">
                      <AlertTriangle className="w-5 h-5 text-orange-600" />
                      Enjeux
                    </h4>
                    <p className="text-sm text-gray-700 mb-2">Défis et contraintes à considérer:</p>
                    <ul className="space-y-2 text-sm text-gray-700">
                      <li>• Contraintes budgétaires ou de ressources</li>
                      <li>• Dépendances bloquantes ou problèmes techniques</li>
                      <li>• Points nécessitant une résolution rapide</li>
                    </ul>
                  </div>

                  <div className="bg-red-50 border-2 border-red-200 rounded-xl p-6">
                    <h4 className="font-bold text-lg mb-3 flex items-center gap-2">
                      <Target className="w-5 h-5 text-red-600" />
                      Risques
                    </h4>
                    <p className="text-sm text-gray-700 mb-2">Menaces potentielles à surveiller:</p>
                    <ul className="space-y-2 text-sm text-gray-700">
                      <li>• Risques identifiés pour le projet ou l'organisation</li>
                      <li>• Problèmes potentiels et impacts négatifs</li>
                      <li>• Plans de mitigation ou actions préventives</li>
                    </ul>
                  </div>

                  <div className="bg-purple-50 border-2 border-purple-200 rounded-xl p-6">
                    <h4 className="font-bold text-lg mb-3 flex items-center gap-2">
                      <Lightbulb className="w-5 h-5 text-purple-600" />
                      Conseils professionnels
                    </h4>
                    <ul className="space-y-2 text-sm text-gray-700">
                      <li>• Soyez concis et factuel dans vos notes</li>
                      <li>• Notez l'essentiel en temps réel pendant la réunion</li>
                      <li>• Identifiez clairement les responsables et les échéances</li>
                      <li>• Les détails complets peuvent être ajoutés dans les documents joints</li>
                    </ul>
                  </div>
                </div>

                <div className="mt-6 flex justify-end">
                  <button
                    onClick={() => setShowGuide(false)}
                    className="px-6 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
                  >
                    Compris!
                  </button>
                </div>
              </motion.div>
            </div>
          )}

          {/* GUIDE IA MODAL */}
          {showGuideIA && (
            <div className="absolute inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={() => setShowGuideIA(false)}>
              <motion.div
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                className="bg-white rounded-2xl p-8 max-w-4xl w-full max-h-[80vh] overflow-y-auto"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="flex justify-between items-center mb-6">
                  <div className="bg-gradient-to-r from-blue-600 to-purple-600 -mx-8 -mt-8 p-6 mb-6 rounded-t-2xl">
                    <h3 className="text-2xl font-bold text-white flex items-center gap-2">
                      <Brain className="w-7 h-7" />
                      Guide de Génération Intelligente (IA)
                    </h3>
                  </div>
                  <button onClick={() => setShowGuideIA(false)} className="absolute top-6 right-6 p-2 bg-white/20 hover:bg-white/30 rounded-lg text-white">
                    <X className="w-5 h-5" />
                  </button>
                </div>

                <div className="space-y-6 mt-6">
                  <div>
                    <h4 className="font-bold text-lg mb-3 flex items-center gap-2">
                      <Sparkles className="w-5 h-5 text-purple-600" />
                      COMMENT ÇA FONCTIONNE?
                    </h4>
                    <p className="text-gray-700 leading-relaxed">
                      L'intelligence artificielle analyse vos <strong>notes, décisions, enjeux et actions</strong> pour créer un résumé professionnel et structuré. 
                      Le résumé est généré selon les meilleures pratiques de gestion de réunion et suit une structure claire et professionnelle.
                    </p>
                  </div>

                  <div className="bg-green-50 border-2 border-green-200 rounded-xl p-6">
                    <h4 className="font-bold text-lg mb-3 flex items-center gap-2">
                      <Shield className="w-5 h-5 text-green-600" />
                      CONFIDENTIALITÉ ET SÉCURITÉ
                    </h4>
                    <p className="text-sm text-gray-700 leading-relaxed">
                      Aucune donnée sensible n'est transmise à l'IA. Seuls les titres et descriptions génériques sont utilisés pour générer le résumé. 
                      Les informations confidentielles restent sécurisées dans votre base de données locale.
                    </p>
                  </div>

                  <div>
                    <h4 className="font-bold text-lg mb-3 flex items-center gap-2">
                      <Award className="w-5 h-5 text-green-600" />
                      STANDARD NIST AI (Cadre BZ)
                    </h4>
                    <p className="text-gray-700 mb-4">
                      Nous respectons le cadre <strong>NIST (National Institute of Standards and Technology)</strong> pour une IA éthique, responsable et transparente.
                    </p>
                    <div className="grid grid-cols-3 gap-4">
                      <div className="bg-white border border-gray-200 rounded-lg p-4">
                        <Search className="w-6 h-6 text-blue-600 mb-2" />
                        <h5 className="font-bold text-sm mb-1">Transparence</h5>
                        <p className="text-xs text-gray-600">Vous savez toujours quand un contenu est généré par IA</p>
                      </div>
                      <div className="bg-white border border-gray-200 rounded-lg p-4">
                        <CheckCircle className="w-6 h-6 text-green-600 mb-2" />
                        <h5 className="font-bold text-sm mb-1">Responsabilité</h5>
                        <p className="text-xs text-gray-600">L'humain valide et approuve toujours le résultat</p>
                      </div>
                      <div className="bg-white border border-gray-200 rounded-lg p-4">
                        <Shield className="w-6 h-6 text-purple-600 mb-2" />
                        <h5 className="font-bold text-sm mb-1">Fiabilité</h5>
                        <p className="text-xs text-gray-600">Basé sur des standards reconnus mondialement</p>
                      </div>
                    </div>
                  </div>

                  <div className="bg-blue-100 border-2 border-blue-300 rounded-xl p-4 flex items-center gap-3">
                    <Award className="w-8 h-8 text-blue-600 flex-shrink-0" />
                    <span className="font-bold text-blue-900">Conforme au Standard NIST AI - Qualité BZ</span>
                  </div>
                </div>

                <div className="mt-6 flex justify-end">
                  <button
                    onClick={() => setShowGuideIA(false)}
                    className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    Compris!
                  </button>
                </div>
              </motion.div>
            </div>
          )}
        </motion.div>
      </div>
    </AnimatePresence>
  );
};

export default ComiteTIReunionsModal;

